/**
 * Order mailing — templates, rules and the decision of whether a rule fires.
 *
 * The engine is three pure questions, and all three live here so they can be tested without a
 * database: did the event match the rule's trigger, do the rule's conditions hold for this order,
 * and — if not — **why not**, in words a person reading the history can act on.
 *
 * That last one is not decoration. The most likely support question about this feature is "why
 * did this customer not get the message", and without a recorded reason the answer is a search
 * through logs. Hence `MailingSkipReason` and the fact that a non-match is *stored* rather than
 * silently dropped.
 */

import type { MailingChannel } from './mailing.types';
import { CONDITION_FIELDS, VARIABLE_BY_TOKEN, type OrderMailingVariable } from './orderMailing.variables';
import type { OrderMailingContext } from './orderMailing.context';

/* ------------------------------------------------------------------ */
/* Templates                                                           */
/* ------------------------------------------------------------------ */

export const MESSAGE_TEMPLATE_STATUSES = ['active', 'draft', 'archived'] as const;
export type MessageTemplateStatus = (typeof MESSAGE_TEMPLATE_STATUSES)[number];

export const MESSAGE_TEMPLATE_STATUS_LABELS: Record<MessageTemplateStatus, string> = {
  active: 'פעילה',
  draft: 'טיוטה',
  archived: 'בארכיון'
};

export interface MessageTemplateDto {
  id: number;
  name: string;
  description: string | null;
  channel: MailingChannel;
  subject: string | null;
  body: string;
  /**
   * Who the message comes from, kept **on the template**.
   *
   * The sender is part of what somebody composes once and reuses, not a field to re-pick in every
   * rule that uses the template. `null` means the server default address; for SMS it means the
   * `Malash` alias that `utils/inforu` already falls back to.
   */
  emailAccountId: number | null;
  senderName: string | null;
  status: MessageTemplateStatus;
  createdByUserId: number | null;
  createdAt: string;
  updatedAt: string;
  /** How many active rules point at it — a template in use must not be deleted by surprise. */
  rulesCount?: number;
}

export interface UpsertMessageTemplateBody {
  name: string;
  description?: string | null;
  channel: MailingChannel;
  subject?: string | null;
  body: string;
  emailAccountId?: number | null;
  senderName?: string | null;
  status?: MessageTemplateStatus;
}

/* ------------------------------------------------------------------ */
/* Triggers                                                            */
/* ------------------------------------------------------------------ */

/**
 * What happened to an order. Also the rule's trigger — one vocabulary, so a rule cannot name an
 * event that is never emitted.
 */
export const ORDER_EVENT_TYPES = [
  'order_created',
  'order_status_changed',
  'order_fulfillment_status_changed'
] as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPES)[number];

export const ORDER_EVENT_TYPE_LABELS: Record<OrderEventType, string> = {
  order_created: 'הזמנה נקלטה',
  order_status_changed: 'שינוי סטטוס הזמנה',
  order_fulfillment_status_changed: 'שינוי סטטוס אספקה'
};

/**
 * **There is deliberately no `order_payment_status_changed`.**
 *
 * `paymentStatus` is written once, at intake, from whatever the shop's checkout reported, and
 * nothing in this system ever changes it afterwards — §22 keeps charging and refunds out of the
 * orders module. Offering the trigger anyway would let somebody build a rule, activate it, and
 * wait for a message that can never be sent. Add the value here on the day a payment webhook
 * starts moving that column, and not before.
 *
 * The status is still usable as a **condition** (`{paymentStatus}` in the catalog), which is the
 * part that does work: "when the order ships **and** it is paid".
 */

/**
 * Which status list the rule's `fromStatus`/`toStatus` are drawn from.
 *
 * `null` for `order_created`, which has no transition — a rule on it names no statuses, and the
 * dialog hides the two selects rather than offering a choice that is ignored.
 */
export const TRIGGER_STATUS_AXIS: Record<OrderEventType, 'order' | 'fulfillment' | null> = {
  order_created: null,
  order_status_changed: 'order',
  order_fulfillment_status_changed: 'fulfillment'
};

/* ------------------------------------------------------------------ */
/* Conditions                                                          */
/* ------------------------------------------------------------------ */

export const MAILING_CONDITION_OPERATORS = ['in', 'not_in', 'gte', 'lte', 'contains'] as const;
export type MailingConditionOperator = (typeof MAILING_CONDITION_OPERATORS)[number];

export const MAILING_CONDITION_OPERATOR_LABELS: Record<MailingConditionOperator, string> = {
  in: 'הוא אחד מ',
  not_in: 'אינו אחד מ',
  gte: 'לפחות',
  lte: 'עד',
  contains: 'מכיל'
};

/** Which operators a field's type allows. The dialog reads this; so does the validator. */
export const OPERATORS_BY_CONDITION_TYPE: Record<string, MailingConditionOperator[]> = {
  select: ['in', 'not_in'],
  number: ['gte', 'lte'],
  text: ['contains']
};

export interface MailingCondition {
  /** A token from the variable catalog — the same string a template writes as `{token}`. */
  field: string;
  operator: MailingConditionOperator;
  values: (string | number)[];
  /** Category only. Defaults to true, so a parent category catches everything under it. */
  includeDescendants?: boolean;
}

const asNumber = (value: string | number): number => (typeof value === 'number' ? value : Number(value));

/**
 * One condition against one order.
 *
 * `in` is an **intersection test**, not equality: an order's value for an item-level field is a
 * list (three products, two categories), and the question is whether any of them appears in the
 * rule's list. `not_in` is the same test negated — an order whose lines are spread over four
 * categories is excluded by naming any one of them, which is the reading a person expects from
 * "אינו אחד מ".
 */
export const evaluateCondition = (condition: MailingCondition, ctx: OrderMailingContext): boolean => {
  const variable: OrderMailingVariable | undefined = VARIABLE_BY_TOKEN[condition.field];
  if (!variable?.conditionValues) return false;

  const actual = variable.conditionValues(ctx, { includeDescendants: condition.includeDescendants !== false });

  switch (condition.operator) {
    case 'in':
      return actual.some(value => condition.values.indexOf(value) !== -1);
    case 'not_in':
      return !actual.some(value => condition.values.indexOf(value) !== -1);
    case 'gte': {
      const threshold = asNumber(condition.values[0]);
      return actual.some(value => asNumber(value) >= threshold);
    }
    case 'lte': {
      const threshold = asNumber(condition.values[0]);
      return actual.some(value => asNumber(value) <= threshold);
    }
    case 'contains': {
      const needles = condition.values.map(value => String(value).toLowerCase().trim()).filter(Boolean);
      return actual.some(value => needles.some(needle => String(value).toLowerCase().indexOf(needle) !== -1));
    }
    default:
      return false;
  }
};

export interface ConditionsVerdict {
  matched: boolean;
  /** The first condition that failed — what the history shows instead of "no match". */
  failedField?: string;
  failedLabel?: string;
}

/** AND across the list. An empty list matches, which is what "no conditions" has to mean. */
export const evaluateConditions = (
  conditions: MailingCondition[],
  ctx: OrderMailingContext
): ConditionsVerdict => {
  for (const condition of conditions) {
    if (evaluateCondition(condition, ctx)) continue;
    const variable = VARIABLE_BY_TOKEN[condition.field];
    return {
      matched: false,
      failedField: condition.field,
      failedLabel: variable?.conditionLabel ?? variable?.label ?? condition.field
    };
  }
  return { matched: true };
};

/**
 * Whether an order's lines were ever matched to the catalogue.
 *
 * Checked **before** the conditions, so that a rule about a category which does not fire on an
 * order full of unrecognised SKUs reports the actual cause. Without this the history would say
 * "קטגוריה לא התאימה" about an order that has no categories at all, and somebody would go looking
 * for the wrong bug.
 */
export const hasUnmatchedLinesOnly = (ctx: OrderMailingContext): boolean =>
  ctx.items.length > 0 && ctx.items.every(item => !item.matched);

/** Which condition fields cannot be answered for an order whose lines never matched. */
export const CATALOGUE_DEPENDENT_FIELDS: string[] = CONDITION_FIELDS.filter(field =>
  ['category', 'tag', 'brand', 'product'].indexOf(field.optionSource ?? '') !== -1
).map(field => field.token);

/* ------------------------------------------------------------------ */
/* Rules                                                               */
/* ------------------------------------------------------------------ */

export interface MailingRuleDto {
  id: number;
  name: string;
  active: boolean;
  /** Send order when several rules fire on one event — not a winner. See the two-messages rule. */
  priority: number;
  templateId: number;
  templateName: string;
  channel: MailingChannel;
  triggerType: OrderEventType;
  fromStatus: string | null;
  toStatus: string | null;
  conditions: MailingCondition[];
  /** Off by default: a fulfillment status bounces, and a rule that re-sends on every bounce spams. */
  allowRepeat: boolean;
  sentCount: number;
  lastSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertMailingRuleBody {
  name: string;
  templateId: number;
  triggerType: OrderEventType;
  fromStatus?: string | null;
  toStatus?: string | null;
  conditions?: MailingCondition[];
  allowRepeat?: boolean;
  active?: boolean;
  priority?: number;
}

export interface OrderEventInput {
  type: OrderEventType;
  fromValue: string | null;
  toValue: string | null;
}

/**
 * Does the event this rule was woken by match what the rule asked for?
 *
 * `fromStatus: null` means "from anything", which is the common case — most rules care only about
 * what the order became. Naming a `from` as well is how you express "only the first time it
 * becomes shipped", and it is why the outbox records both values.
 */
export const triggerMatches = (
  rule: Pick<MailingRuleDto, 'triggerType' | 'fromStatus' | 'toStatus'>,
  event: OrderEventInput
): boolean => {
  if (rule.triggerType !== event.type) return false;
  if (rule.fromStatus && rule.fromStatus !== event.fromValue) return false;
  if (rule.toStatus && rule.toStatus !== event.toValue) return false;
  return true;
};

/**
 * **There is no scheduled send.** Every rule fires on the event that woke it.
 *
 * A delay was designed and then dropped on request: "כל שינוי סטטוס שישלח הודעה". Removing it took
 * a whole state out of the engine — an event can no longer be *waiting*, so every rule reaches a
 * decision on the first pass and the outbox holds only work that has not been done yet, never work
 * that is not due yet. Re-introducing it means reviving that state, not adding a column.
 */

/* ------------------------------------------------------------------ */
/* Send log                                                            */
/* ------------------------------------------------------------------ */

export const AUTOMATION_SEND_STATUSES = ['sent', 'skipped', 'failed'] as const;
export type AutomationSendStatus = (typeof AUTOMATION_SEND_STATUSES)[number];

export const AUTOMATION_SEND_STATUS_LABELS: Record<AutomationSendStatus, string> = {
  sent: 'נשלח',
  skipped: 'לא נשלח',
  failed: 'נכשל'
};

/**
 * Why a rule that was woken did not send. Stored, and shown in the history.
 *
 * `unmatched_lines` earns its own value rather than being folded into `conditions`: the two look
 * identical in the data and lead to completely different actions — one is "the rule is wrong",
 * the other is "the SKU is not in our catalogue".
 */
export const MAILING_SKIP_REASONS = [
  'already_sent',
  'conditions',
  'unmatched_lines',
  'order_cancelled',
  'template_inactive',
  'no_recipient'
] as const;
export type MailingSkipReason = (typeof MAILING_SKIP_REASONS)[number];

export const MAILING_SKIP_REASON_LABELS: Record<MailingSkipReason, string> = {
  already_sent: 'הכלל הזה כבר נשלח להזמנה הזאת',
  conditions: 'התנאים של הכלל לא התאימו להזמנה',
  unmatched_lines: 'שורות ההזמנה לא הותאמו לקטלוג, ולכן אין להן קטגוריה או תגית',
  order_cancelled: 'ההזמנה בוטלה לפני מועד השליחה',
  template_inactive: 'התבנית אינה פעילה',
  no_recipient: 'אין להזמנה כתובת או טלפון לשליחה בערוץ הזה'
};

export interface OrderMailingSendDto {
  id: number;
  ruleId: number | null;
  ruleName: string | null;
  orderId: number;
  orderNumber: string;
  customerName: string;
  channel: MailingChannel;
  status: AutomationSendStatus;
  skipReason: MailingSkipReason | null;
  /** The provider's own error, when one came back. */
  errorMessage: string | null;
  mailingMessageId: number | null;
  sentAt: string | null;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Preview and back-test                                               */
/* ------------------------------------------------------------------ */

export interface TemplatePreviewResult {
  subject: string;
  body: string;
  /** The order the preview was rendered against, so the dialog can say which one it used. */
  orderNumber: string;
  unknownTokens: string[];
  emptyTokens: string[];
}

/**
 * "How many of the last 30 days' orders would this rule have caught."
 *
 * A complex condition list written without this number is written blind, and the number is cheap:
 * the same evaluation the worker runs, over orders we already have.
 */
export interface RuleBacktestResult {
  windowDays: number;
  ordersChecked: number;
  matched: number;
  /** Why the rest fell out, so a rule that catches nothing says which condition did it. */
  skippedByField: { field: string; label: string; count: number }[];
}
